import { Stack } from '@customink/pigment-react';
import { useMemo } from 'react';
import { trackLoginSubmit } from '~/adapter/tracking/loginAndSignup';
import { interaction } from '~/adapter/tracking/authEvents';
import { trackEvent } from '~/adapter/tracking/trackEvent';
import {
    login_code_sign_in_path,
    new_user_session_path,
    user_facebook_omniauth_authorize_path,
    user_google_oauth2_omniauth_authorize_path,
} from '~/adapter/window/routes';
import { persistDefaultEmail } from '~/components/hooks/useDefaultEmail';
import { BrandButton, ColoredBrandButton } from './BrandButton';
import { Facebook } from './icons/Facebook';
import { Google } from './icons/Google';
import { MagicWand } from './icons/MagicWand';
import { PasswordLock } from './icons/PasswordLock';
import { Link } from './Link';

interface OAuthLoginProps {
    euRequest: boolean;
    enablePasswordLogin?: boolean;
    enablePasswordlessLogin?: boolean;
    enableSocialLogin?: boolean;
    userEmail?: string;
    step?: 'email_entry' | 'password_entry';
}

export function LoginAlternatives({
    userEmail,
    euRequest,
    enablePasswordLogin = true,
    enablePasswordlessLogin = true,
    enableSocialLogin = true,
    step = 'email_entry',
}: OAuthLoginProps) {
    const enableSocialLoginInEU = useMemo(
        () => new URLSearchParams(window.location.search).has('enableSocialLoginInEU'),
        [enableSocialLogin],
    );
    const showSocialLogin = enableSocialLogin && (!euRequest || enableSocialLoginInEU);
    return (
        <>
            <Stack gap={2}>
                {enablePasswordLogin && (
                    <Link
                        onClick={() => {
                            persistDefaultEmail(userEmail);
                            interaction(step, 'flow_switch_clicked');
                        }}
                        underline="none"
                        href={new_user_session_path()}
                    >
                        <ColoredBrandButton onClick={trackEvent('Password login')}>
                            <PasswordLock width={20} height={20} />
                            <span style={{ marginLeft: 5 }}>Sign in with Password</span>
                        </ColoredBrandButton>
                    </Link>
                )}
                {enablePasswordlessLogin && (
                    <Link
                        onClick={() => {
                            persistDefaultEmail(userEmail);
                            interaction(step, 'one_time_code_clicked');
                        }}
                        underline="none"
                        href={login_code_sign_in_path()}
                    >
                        <ColoredBrandButton onClick={trackEvent('Request Code')}>
                            <MagicWand width={20} height={20} />
                            <span style={{ marginLeft: 5 }}>Sign in with a one-time code</span>
                        </ColoredBrandButton>
                    </Link>
                )}
                {showSocialLogin && (
                    <a href={user_google_oauth2_omniauth_authorize_path()}>
                        <BrandButton
                            onClick={(e) => {
                                trackEvent('Continue Google')(e);
                                trackLoginSubmit('Google');
                                interaction(step, 'social_cta_clicked', 'google');
                            }}
                        >
                            <Google width={20} height={20} />
                            <span style={{ marginLeft: 5 }}>Continue with Google</span>
                        </BrandButton>
                    </a>
                )}
                {showSocialLogin && (
                    <a href={user_facebook_omniauth_authorize_path()}>
                        <BrandButton
                            onClick={(e) => {
                                trackEvent('Continue FB')(e);
                                trackLoginSubmit('Facebook');
                                interaction(step, 'social_cta_clicked', 'facebook');
                            }}
                        >
                            <Facebook width={20} height={20} />
                            <span style={{ marginLeft: 5 }}>Continue with Facebook</span>
                        </BrandButton>
                    </a>
                )}
            </Stack>
        </>
    );
}
